import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import soffittoImg from '../Assets/Image/SOFFITTO.jpg';
import pro from '../Assets/Image/esiproB.png';
import Carousel from "../Components/Carousel/Carousel.js";
import introVideo from "../Assets/Videos/intro.mp4";
import HeaderHero from "../Components/Common/HeaderHero";
import Footer from "../Components/Common/Footer";
import evoluzione02 from "../Assets/Image/about/in-evoluzione/02-interno.png";
import evoluzione03 from "../Assets/Image/about/in-evoluzione/03-render-prova.png";
import evoluzione04 from "../Assets/Image/about/in-evoluzione/04-risorsa.png";
import evoluzione05 from "../Assets/Image/about/in-evoluzione/05-render-ravenna.png";
import metodo01 from "../Assets/Image/about/metodo/01-studio.png";
import metodo02 from "../Assets/Image/about/metodo/02-riunione.png";

const evoluzioneImages = [
    evoluzione02,
    evoluzione03,
    evoluzione04,
    evoluzione05,
];

const metodoImages = [
    metodo01,
    metodo02,
];

function useIsMobile(breakpoint = 768) {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [breakpoint]);
    return isMobile;
}

export default function ChiSiamo() {
    const containerRef = useRef(null);
    const { t } = useTranslation();
    const isMobile = useIsMobile();

    const scrollToContent = () => {
        const container = containerRef.current;
        if (container) {
            container.scrollBy({ top: container.clientHeight, behavior: 'smooth' });
        }
    };

    return (
        <div
            ref={containerRef}
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                height: 'calc(100vh - 66px)',
                height: 'calc(100dvh - 66px)',
                background: '#fff',
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 300,
                overflowY: 'auto',
                scrollSnapType: 'y proximity',
                scrollBehavior: 'smooth',
            }}
        >
            {/* SEZIONE 1: Hero + freccia */}
            <div style={{
                scrollSnapAlign: 'start',
                width: '100%',
                height: isMobile ? '60vh' : '100%',
                minHeight: isMobile ? '60vh' : '100%',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
            }}>
                <div style={{ flex: 1, minHeight: 0 }}>
                    <HeaderHero
                        className="esi-transition-header"
                        bgImage={soffittoImg}
                        logo={pro}
                        overlayMs={950}
                    />
                </div>

                <div
                    onClick={scrollToContent}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '16px 0 40px',
                        cursor: 'pointer',
                        flexShrink: 0,
                    }}
                >
                    <motion.div
                        animate={{ y: [0, 10, 0] }}
                        transition={{
                            duration: 1.5,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                    >
                        <ChevronDown size={36} color="rgb(152,25,20)" strokeWidth={2} />
                    </motion.div>
                </div>
            </div>

            {/* SEZIONE 2: Intro text */}
            <div
                                style={{
                    scrollSnapAlign: 'start',
                    width: '100%',
                    maxWidth: '1200px',
                    margin: '0 auto',
                    padding: '40px 5%',
                    flexShrink: 0,
                }}
            >
                <p style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, textAlign: 'justify' }}>
                    {t('about.intro')}
                </p>
            </div>

            {/* ROW 1 */}
            <div
                                style={{
                    width: '100%',
                    maxWidth: '1200px',
                    margin: '0 auto',
                    padding: '20px 5%',
                }}
            >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'clamp(16px, 4vw, 40px)', alignItems: 'stretch' }}>
                    <div style={{ flex: 1, minWidth: 'min(100%, 280px)' }}>
                        <h5 style={{ color: 'rgb(152,25,20)', marginTop: 0, fontFamily: "'Roboto', sans-serif", fontWeight: 400 }}>{t('about.section1.title')}</h5>
                        <p style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, textAlign: 'justify' }}>
                            {t('about.section1.text')}
                        </p>
                    </div>
                    <div
                        style={{
                            flex: 1,
                            minWidth: 'min(100%, 280px)',
                            display: 'flex',
                            alignItems: 'stretch',
                            justifyContent: 'center',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                    >
                        <Carousel images={evoluzioneImages} altPrefix={t('about.section1.title')} />
                    </div>
                </div>
            </div>

            {/* ROW 2 */}
            <div
                                style={{
                    width: '100%',
                    maxWidth: '1200px',
                    margin: '0 auto',
                    padding: '20px 5%',
                }}
            >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'clamp(16px, 4vw, 40px)', alignItems: 'stretch' }}>
                    <div
                        style={{
                            flex: 1,
                            minWidth: 'min(100%, 280px)',
                            display: 'flex',
                            alignItems: 'stretch',
                            justifyContent: 'center',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                    >
                        <video
                            src={introVideo}
                            controls
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '8px',
                            }}
                        />
                    </div>
                    <div style={{ flex: 1, minWidth: 'min(100%, 280px)' }}>
                        <h5 style={{ color: 'rgb(152,25,20)', marginTop: 0, fontFamily: "'Roboto', sans-serif", fontWeight: 400 }}>{t('about.section2.title')}</h5>
                        <p style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, textAlign: 'justify' }}>
                            {t('about.section2.text')}
                        </p>
                    </div>
                </div>
            </div>

            {/* ROW 3 */}
            <div
                                style={{
                    width: '100%',
                    maxWidth: '1200px',
                    margin: '0 auto',
                    padding: '20px 5%',
                }}
            >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'clamp(16px, 4vw, 40px)', alignItems: 'stretch' }}>
                    <div style={{ flex: 1, minWidth: 'min(100%, 280px)' }}>
                        <h5 style={{ color: 'rgb(152,25,20)', marginTop: 0, fontFamily: "'Roboto', sans-serif", fontWeight: 400 }}>{t('about.section3.title')}</h5>
                        <p style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, textAlign: 'justify' }}>
                            {t('about.section3.text')}
                        </p>
                    </div>
                    <div
                        style={{
                            flex: 1,
                            minWidth: 'min(100%, 280px)',
                            display: 'flex',
                            alignItems: 'stretch',
                            justifyContent: 'center',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                    >
                        <Carousel images={metodoImages} altPrefix={t('about.section3.title')} />
                    </div>
                </div>
            </div>

            {/* FOOTER */}
            <div style={{ width: '100%', marginTop: '60px' }}>
                <Footer />
            </div>
        </div>
    );
}
